import { useQuery } from "@apollo/client";
import { useEffect, useMemo, useState } from "react";

import { GET_ORDER_LISTING_HISTORY, HistoryOrderListResponse } from "../graphql/queries/getOrderListingHistory";
import { buildCentralParcelQueryVariables } from "../utils/buildCentralParcelQueryVariables";
import { stripExceptionFilters } from "../utils/stripExceptionFilters";
import { OrderType } from "../../../../graphql/types/OrderType";
import { OrderFiltersType } from "../../../../graphql/types/OrderFiltersType";
import { ParcelQueryResult } from "./useCentralParcelQuery";

/**
 * 历史包裹查询（已归档订单）。
 *
 * 与 useCentralParcelQuery 的差别只有两点：打的是 getOrderListingHistory，
 * 且请求前剥掉异常信息相关筛选条件。变量构建逻辑与列表页共用同一个函数。
 *
 * 后端对异常筛选字段本身也会忽略，这里剥掉是为了别把无效条件发出去
 * ——请求体更干净，排查问题时不会误以为条件生效了。
 */
export const useCentralParcelHistoryQuery = (
    filters: OrderFiltersType,
    currentPage: number,
    pageSize: number,
    sortField?: string | null,
    sortDirection?: 'ASC' | 'DESC' | null
): ParcelQueryResult => {
    const [parcelData, setParcelData] = useState<OrderType[]>([]);
    const [loading, setLoading] = useState(true);
    const [total, setTotal] = useState(0);
    const [accessiblePages, setAccessiblePages] = useState(0);

    const buildQueryVariables = () =>
        buildCentralParcelQueryVariables(
            stripExceptionFilters(filters),
            currentPage,
            pageSize,
            sortField,
            sortDirection
        );

    const memoizedVariables = useMemo(
        () => buildQueryVariables(),
        [filters, currentPage, pageSize, sortField, sortDirection]
    );

    const { data, loading: queryLoading, refetch } = useQuery<HistoryOrderListResponse>(
        GET_ORDER_LISTING_HISTORY,
        { variables: memoizedVariables }
    );

    useEffect(() => {
        setLoading(queryLoading);
        if (data?.getOrderListingHistory) {
            setParcelData(data.getOrderListingHistory.content ?? []);
            setTotal(data.getOrderListingHistory.totalElements ?? 0);
            setAccessiblePages(data.getOrderListingHistory.accessiblePages ?? 0);
        }
    }, [data, queryLoading]);

    return {
        parcelData,
        loading,
        refetch,
        totalElements: total,
        accessiblePages,
        buildQueryVariables
    };
};
